/* eslint-disable max-lines-per-function, complexity, max-lines */
/* Cohesive single-file section component — see project guidelines. */
import { useState } from "react";
import { motion } from "framer-motion";
import { Mic } from "lucide-react";
import { CEOBookingModal } from "./CEOBookingModal";

const GALLERY = [
    {
        src: "/assets/ceo-hayon-workshop.jpg",
        alt: "Hayón mentoring Young Moguls in a hands-on workshop",
        caption: "Workshop Facilitation",
    },
    {
        src: "/assets/ceo-hayon-stage.jpg",
        alt: "Hayón addressing the Young Mogul team on stage",
        caption: "Leading the Movement",
    },
    {
        src: "/assets/ceo-hayon-africa-child.jpg",
        alt: "Hayón connecting with a child during a community mission",
        caption: "Youth Empowerment",
    },
    {
        src: "/assets/ceo-hayon-occupy-love.jpg",
        alt: "Hayón with the #OccupyLOVE team and children abroad",
        caption: "Global Community Impact",
    },
];

export const CEOFeature = () => {
    const [openBooking, setOpenBooking] = useState(false);

    return (
        <section
            id="ceo"
            data-testid="ceo-section"
            className="relative bg-[#050505] overflow-hidden"
            itemScope
            itemType="https://schema.org/Person"
        >
            {/* Speaker schema microdata */}
            <meta itemProp="name" content="Mickelle Hayón Hellon" />
            <meta itemProp="jobTitle" content="CEO, EDUtainment Pros" />
            <meta itemProp="email" content="EDUtainmentpros@gmail.com" />

            <div className="h-px w-full bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />

            {/* ============ INTRO + PORTRAIT + BIO ============ */}
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-24 sm:pt-32 pb-20">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="flex items-center gap-3 mb-6"
                >
                    <span className="h-px w-10 bg-[#D4AF37]" />
                    <span className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">
                        CEO Vision
                    </span>
                </motion.div>

                <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                    {/* Portrait + Book button */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="lg:col-span-5"
                    >
                        <div className="relative">
                            <div className="relative aspect-[4/5] overflow-hidden border border-[#D4AF37]/40 gold-glow">
                                <img
                                    src="/assets/ceo-hayon-misc.jpg"
                                    alt="Hayón, CEO and Founder of EDUtainment Pros"
                                    loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover object-top"
                                    itemProp="image"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent pointer-events-none" />
                                <div className="absolute bottom-0 left-0 right-0 p-6">
                                    <div className="text-[11px] uppercase tracking-[0.4em] text-white font-bold mb-2">
                                        CEO / Founder
                                    </div>
                                    <div className="font-display text-4xl uppercase tracking-tight leading-none gold-text-gradient font-bold">
                                        Hayón
                                    </div>
                                </div>
                            </div>
                            <span className="absolute -top-3 -left-3 h-7 w-7 border-t-2 border-l-2 border-[#D4AF37]" />
                            <span className="absolute -bottom-3 -right-3 h-7 w-7 border-b-2 border-r-2 border-[#D4AF37]" />
                        </div>

                        {/* Book Hayón — primary CTA button under portrait */}
                        <button
                            type="button"
                            onClick={() => setOpenBooking(true)}
                            data-testid="ceo-book-btn"
                            className="mt-6 group w-full inline-flex items-center justify-center gap-3 px-6 py-4 bg-[#D4AF37] hover:bg-[#E5C158] text-black text-[12px] uppercase tracking-[0.3em] font-bold transition-all gold-glow-strong"
                        >
                            <Mic size={14} />
                            Book Hayón
                        </button>
                        <p className="mt-2 text-center text-[10px] uppercase tracking-[0.3em] text-white/40">
                            Keynotes · Workshops · Conferences · Panels
                        </p>
                    </motion.div>

                    {/* Title + Bio */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.15 }}
                        className="lg:col-span-7"
                    >
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.95]">
                            <span className="block text-white">
                                Meet The Man
                            </span>
                            <span className="block gold-text-gradient">
                                Behind The Vision
                            </span>
                        </h2>
                        <div className="mt-7 space-y-5 text-base sm:text-lg text-white/75 leading-relaxed">
                            <p itemProp="description">
                                The CEO of EDUtainment Pros, Mickelle
                                &quot;Hayón&quot; Hellon, is a dynamic leader
                                dedicated to transforming lives through
                                education, empowerment, leadership development,
                                and innovative learning experiences. His
                                passion for inspiring growth and creating
                                opportunities has established him as a
                                respected voice in professional development,
                                workforce readiness, community engagement, and
                                organizational leadership.
                            </p>
                            <p>
                                Known for his authentic approach and engaging
                                speaking style, Hayón combines practical
                                experience, strategic insight, and motivational
                                storytelling to connect with audiences from
                                diverse backgrounds. He&apos;s committed to
                                helping individuals, teams, businesses,
                                educational institutions, and community
                                organizations globally, unlock their full
                                potential.
                            </p>
                            <p>
                                Whether addressing students, educators,
                                nonprofit leaders, business professionals,
                                entrepreneurs, or corporate teams, Hayón
                                delivers presentations that educate, inspire,
                                and empower audiences to take action.
                            </p>
                        </div>

                        {/* Leadership Philosophy */}
                        <div className="mt-9 border-l-2 border-[#D4AF37] pl-5 sm:pl-6">
                            <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-2">
                                Leadership Philosophy
                            </div>
                            <p className="text-base sm:text-lg text-white/90 italic leading-relaxed">
                                Education should inspire action. Innovation
                                should create opportunity. Leadership should
                                empower others.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* ============ PHOTO GALLERY — IN THE FIELD ============ */}
            <div className="bg-[#0a0a0a] border-y border-[#D4AF37]/20 py-20">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 15 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="flex items-end justify-between mb-8 gap-4 flex-wrap"
                    >
                        <h3 className="font-display text-2xl sm:text-3xl uppercase tracking-tight text-white">
                            In the <span className="gold-text-gradient">Field</span>
                        </h3>
                        <span className="text-[10px] uppercase tracking-[0.3em] text-white/40 pb-2">
                            Speaking · Mentoring · Connecting
                        </span>
                    </motion.div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        {GALLERY.map((g, i) => (
                            <motion.figure
                                key={g.src}
                                initial={{ opacity: 0, y: 15 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.08, duration: 0.6 }}
                                className="relative group overflow-hidden border border-white/10 hover:border-[#D4AF37]/60 transition-all"
                                data-testid={`ceo-gallery-${i}`}
                            >
                                <div className="aspect-square overflow-hidden bg-black">
                                    <img
                                        src={g.src}
                                        alt={g.alt}
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                    />
                                </div>
                                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black to-transparent p-3 pt-10">
                                    <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
                                        {g.caption}
                                    </div>
                                </figcaption>
                            </motion.figure>
                        ))}
                    </div>
                </div>
            </div>

            <CEOBookingModal
                open={openBooking}
                onClose={() => setOpenBooking(false)}
            />
        </section>
    );
};
